/**
 * LA SESSION DE L'ÉQUIPE, LUE DANS LE JETON DE SESSION (DEV-2306).
 * ============================================================================
 *
 * INTENTION : une session ouverte au mot de passe maître (c'est ainsi que
 * l'équipe regarde un compte client) doit pouvoir revoir ce que l'interrupteur
 * réserve à l'équipe, sans qu'un client puisse s'en donner le droit.
 *
 * Le drapeau `adminHomeys` de la réponse de connexion vit aussi dans le
 * stockage du navigateur (`homeys_admin`) : n'importe qui peut l'y écrire. Il
 * ne sert plus qu'au suivi d'usage. La mention qui décide est celle que le
 * serveur SIGNE dans le jeton de session, sous la clé `staff` : la modifier
 * casse la signature, et le serveur refuse alors le jeton.
 *
 * Le front décode la charge utile sans vérifier la signature (il n'a pas la
 * clé). Ce n'est pas une faiblesse : un jeton retouché à la main affiche au
 * plus un menu, mais chaque appel à l'API sera refusé par le serveur, qui lui
 * vérifie la signature et garde les routes.
 *
 * Fermé par défaut : jeton absent, illisible, sans mention, ou mention d'un
 * autre type que le booléen `true`, tout vaut « pas l'équipe ». Un serveur qui
 * ne pose pas encore la mention donne donc « pas l'équipe », sans erreur.
 */
import { jwtDecode } from "jwt-decode"

/** Nom de la mention dans la charge utile du jeton de session. */
export const MENTION_STAFF_DU_JETON = "staff"

/**
 * Vrai quand le jeton de session porte la mention signée « session de
 * l'équipe ». Ne lève jamais.
 *
 * @param jeton - le jeton de session tel que le store le tient
 */
export function sessionDeLEquipeSelonLeJeton(
    jeton: string | null | undefined
): boolean {
    return mentionStaffDuJeton(jeton) === true
}

/**
 * Ce que dit le jeton sur la session de l'équipe (DEV-2457) : `true` quand il
 * porte la mention signée, `false` quand il est lisible sans la porter, et
 * `null` quand il est absent ou illisible. Le suivi d'usage a besoin de cette
 * troisième valeur : sans jeton lisible, et seulement alors, il se rabat sur le
 * drapeau du stockage du navigateur. Ne lève jamais.
 *
 * @param jeton - le jeton de session tel que le store le tient
 */
export function mentionStaffDuJeton(
    jeton: string | null | undefined
): boolean | null {
    if (typeof jeton !== "string" || jeton.trim() === "") return null
    try {
        const charge = jwtDecode<Record<string, unknown>>(jeton)
        if (charge === null || typeof charge !== "object") return null
        return charge[MENTION_STAFF_DU_JETON] === true
    } catch {
        return null
    }
}
